import React from "react";
import {
  BlockSkeleton,
  DonutChartSkeleton,
  LineChartSkeleton,
  Skeleton,
  TextSkeleton,
} from "../loading";
import "./UISkeletonDocsPage.css";

const toneExamples = [
  {
    tone: "theme",
    title: "Theme",
    description:
      "Usa i colori tema principali. E' il default consigliato per card, pannelli e stati di attesa standard.",
    baseColor: "var(--colChiaro)",
    highlightColor: "var(--colChiarissimo)",
    snippet: `<BlockSkeleton tone="theme" height={18} width="44%" className="mb3" />
<TextSkeleton
  tone="theme"
  lines={3}
  lineHeight={14}
  lastLineWidth="68%"
  className="mb3"
/>
<div className="flex items-center">
  <Skeleton tone="theme" inline width={80} height={12} className="mr2" />
  <Skeleton tone="theme" inline width={56} height={12} />
</div>`,
  },
  {
    tone: "soft",
    title: "Soft",
    description:
      "Versione piu' morbida e poco contrastata. Utile dentro box molto densi o secondari.",
    baseColor: "rgba(0, 0, 0, 0.08)",
    highlightColor: "rgba(255, 255, 255, 0.58)",
    snippet: `<BlockSkeleton tone="soft" height={18} width="44%" className="mb3" />
<TextSkeleton
  tone="soft"
  lines={3}
  lineHeight={14}
  lastLineWidth="68%"
  className="mb3"
/>
<div className="flex items-center">
  <Skeleton tone="soft" inline width={80} height={12} className="mr2" />
  <Skeleton tone="soft" inline width={56} height={12} />
</div>`,
  },
  {
    tone: "neutral",
    title: "Neutral",
    description:
      "Palette neutra grigio chiaro. Utile quando il tema di sfondo e' gia' molto colorato.",
    baseColor: "#e5e7eb",
    highlightColor: "#f3f4f6",
    snippet: `<BlockSkeleton tone="neutral" height={18} width="44%" className="mb3" />
<TextSkeleton
  tone="neutral"
  lines={3}
  lineHeight={14}
  lastLineWidth="68%"
  className="mb3"
/>
<div className="flex items-center">
  <Skeleton tone="neutral" inline width={80} height={12} className="mr2" />
  <Skeleton tone="neutral" inline width={56} height={12} />
</div>`,
  },
  {
    tone: "alt",
    title: "Alt",
    description:
      "Usa il colore alternativo del tema. Utile per placeholder in sezioni secondarie o stati alternativi.",
    baseColor: "var(--colAlt)",
    highlightColor: "#ffecec",
    snippet: `<BlockSkeleton tone="alt" height={18} width="44%" className="mb3" />
<TextSkeleton
  tone="alt"
  lines={3}
  lineHeight={14}
  lastLineWidth="68%"
  className="mb3"
/>
<div className="flex items-center">
  <Skeleton tone="alt" inline width={80} height={12} className="mr2" />
  <Skeleton tone="alt" inline width={56} height={12} />
</div>`,
  },
  {
    tone: "contrast",
    title: "Contrast",
    description:
      "Versione piu' contrastata basata su --colMedio. Utile quando vuoi che il placeholder resti leggibile anche su superfici molto chiare o ampie.",
    baseColor: "var(--colMedio)",
    highlightColor: "var(--colChiarissimo)",
    snippet: `<BlockSkeleton tone="contrast" height={18} width="44%" className="mb3" />
<TextSkeleton
  tone="contrast"
  lines={3}
  lineHeight={14}
  lastLineWidth="68%"
  className="mb3"
/>
<div className="flex items-center">
  <Skeleton tone="contrast" inline width={80} height={12} className="mr2" />
  <Skeleton tone="contrast" inline width={56} height={12} />
</div>`,
  },
];

function DocsCard({ title, children, className = "" }) {
  return (
    <section
      className={`domino-ui-docs-card sfondoBiancoTema br3 ba b--black-10 pa3 ${className}`.trim()}
    >
      <h3 className="mt0 mb2">{title}</h3>
      {children}
    </section>
  );
}

function CodeExample({ children }) {
  return (
    <pre className="domino-ui-docs-code pa3 br3 bg-near-white overflow-auto">
      {children}
    </pre>
  );
}

function ToneShowcaseCard({
  tone,
  title,
  description,
  baseColor,
  highlightColor,
  snippet,
}) {
  return (
    <DocsCard title={title}>
      <p className="mt0 mb3 dark-gray">{description}</p>
      <div className="domino-ui-docs-example flex flex-column justify-between">
        <div>
          <BlockSkeleton tone={tone} height={18} width="44%" className="mb3" />
          <TextSkeleton
            tone={tone}
            lines={3}
            lineHeight={14}
            lastLineWidth="68%"
            className="mb3"
          />
          <div className="flex items-center">
            <Skeleton
              tone={tone}
              inline
              width={80}
              height={12}
              className="mr2"
            />
            <Skeleton tone={tone} inline width={56} height={12} />
          </div>
        </div>
      </div>
      <div className="mt3 pt2 bt b--black-10">
        <div className="flex items-center mb2">
          <span
            className="domino-ui-docs-tone-swatch mr2"
            style={{ backgroundColor: baseColor }}
          />
          <span className="f7">Base: {baseColor}</span>
        </div>
        <div className="flex items-center mb3">
          <span
            className="domino-ui-docs-tone-swatch mr2"
            style={{ backgroundColor: highlightColor }}
          />
          <span className="f7">Highlight: {highlightColor}</span>
        </div>
        <CodeExample>{snippet}</CodeExample>
      </div>
    </DocsCard>
  );
}

function UISkeletonDocsPage({ utente }) {
  if (!utente?.permessiAPI?.["read:debug"]) {
    return (
      <div className="pa2 pa3-ns">
        <div className="sfondoBiancoTema br3 pa3">
          <h3 className="mt0 mb2">Componenti UI / Skeleton</h3>
          <p className="mb0 dark-red">
            Non hai i permessi per visualizzare questa pagina.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pa2 pa3-ns">
      <div className="domino-ui-docs-page">
        <div className="sfondoBiancoTema br3 pa3 pa4-l">
          <div className="mb4">
            <div className="f2 b mb2">Componenti UI</div>
            <div className="f4 mb2">Skeleton</div>
            <p className="mt0 mb2 dark-gray">
              Placeholder standard Domino per contenuti in attesa di risposta
              esterna. L&apos;obiettivo e&apos; simulare la forma del contenuto
              reale, mantenere lo spazio finale e rendere il caricamento piu'
              leggibile di uno spinner generico.
            </p>
            <div className="pa3 br3 bl bw1 bordoColAlt sfondoGrigioChiaroTema">
              <div className="b mb2">Raccomandazioni d&apos;uso</div>
              <ul className="mv0 pl3">
                <li>
                  Usa `TextSkeleton` per testi, metadati, righe tabellari e
                  liste.
                </li>
                <li>
                  Usa `BlockSkeleton` per card, grafici, box, immagini e pulsanti
                  grandi.
                </li>
                <li>
                  Usa `Skeleton` quando serve controllo fine su dimensioni,
                  inline o classi custom.
                </li>
                <li>
                  Mantieni width e height vicini al contenuto finale per evitare
                  layout shift.
                </li>
                <li>
                  Preferisci il tone `theme` come default e cambia tone solo
                  quando il contesto visivo lo richiede davvero.
                </li>
                <li>
                  Per pie, donut e line chart usa skeleton chart dedicati, non
                  placeholder generici.
                </li>
                <li>
                  Se vuoi che il loading sia percepibile in ordine, orchestra
                  le fetch con stati espliciti invece di affidarti a ritardi
                  artificiali.
                </li>
              </ul>
            </div>
          </div>

          <DocsCard title="Tones" className="mb4">
            <p className="mt0 dark-gray">
              Ogni skeleton usa due colori: uno di base e uno di highlight per
              la shimmer orizzontale. Il prop `tone` applica una coppia di
              colori gia' pronta.
            </p>
            <CodeExample>
              {`<BlockSkeleton tone="theme" height={48} />
<TextSkeleton tone="soft" lines={3} />
<Skeleton tone="neutral" width={96} height={16} inline />
<BlockSkeleton tone="contrast" height={48} />`}
            </CodeExample>
          </DocsCard>

          <div className="domino-ui-docs-grid mb4">
            {toneExamples.map((example) => (
              <ToneShowcaseCard key={example.tone} {...example} />
            ))}
          </div>

          <div className="domino-ui-docs-grid mb4">
            <DocsCard title="TextSkeleton">
              <p className="mt0 dark-gray">
                Per paragrafi, label e contenuti verticali. `lines` controlla il
                numero di righe, `lineHeight` la loro altezza, `lastLineWidth`
                accorcia l&apos;ultima riga per un effetto piu' naturale.
              </p>
              <div className="domino-ui-docs-example">
                <TextSkeleton lines={4} lineHeight={14} lastLineWidth="54%" />
              </div>
              <CodeExample>
                {
                  '<TextSkeleton lines={4} lineHeight={14} lastLineWidth="54%" />'
                }
              </CodeExample>
            </DocsCard>

            <DocsCard title="BlockSkeleton">
              <p className="mt0 dark-gray">
                Per box, grafici, preview o media. Usa `height` e
                `borderRadius` per avvicinare il piu' possibile il layout
                finale.
              </p>
              <div className="domino-ui-docs-example">
                <BlockSkeleton height={96} borderRadius={16} />
              </div>
              <CodeExample>
                {'<BlockSkeleton height={96} borderRadius={16} />'}
              </CodeExample>
            </DocsCard>

            <DocsCard title="Skeleton Inline">
              <p className="mt0 dark-gray">
                Per pill, numeri, badge o piccoli frammenti dentro una riga.
                Imposta `inline` per non spezzare il flusso del testo.
              </p>
              <div className="domino-ui-docs-example flex items-center">
                <span className="mr2">Totale:</span>
                <Skeleton inline width={48} height={14} className="mr2" />
                <Skeleton inline width={84} height={14} tone="neutral" />
              </div>
              <CodeExample>
                {'<Skeleton inline width={48} height={14} className="mr2" />'}
              </CodeExample>
            </DocsCard>
          </div>

          <div className="domino-ui-docs-grid mb4">
            <DocsCard title="DonutChartSkeleton">
              <p className="mt0 dark-gray">
                Per pie e donut. Si adatta alla larghezza disponibile e usa
                anelli `svg` reali con legenda skeleton sotto.
              </p>
              <div className="domino-ui-docs-example">
                <DonutChartSkeleton
                  tone="soft"
                  height={260}
                  legendRows={4}
                  rings={[
                    { innerRadius: 40, outerRadius: 110, segments: 4 },
                  ]}
                />
              </div>
              <CodeExample>
                {`<DonutChartSkeleton
  tone="soft"
  height={260}
  legendRows={4}
  rings={[
    { innerRadius: 40, outerRadius: 110, segments: 4 },
  ]}
/>`}
              </CodeExample>
            </DocsCard>

            <DocsCard title="Donut Concentrici">
              <p className="mt0 dark-gray">
                Per grafici ad anelli multipli come in dashboard ordini. Ogni
                ring definisce `innerRadius` e `outerRadius`.
              </p>
              <div className="domino-ui-docs-example">
                <DonutChartSkeleton
                  tone="soft"
                  height={260}
                  legendRows={3}
                  rings={[
                    { innerRadius: 80, outerRadius: 110, segments: 4, offset: 0.04 },
                    { innerRadius: 0, outerRadius: 70, segments: 3, offset: 0.1 },
                  ]}
                />
              </div>
              <CodeExample>
                {`<DonutChartSkeleton
  tone="soft"
  height={260}
  legendRows={3}
  rings={[
    { innerRadius: 80, outerRadius: 110, segments: 4, offset: 0.04 },
    { innerRadius: 0, outerRadius: 70, segments: 3, offset: 0.1 },
  ]}
/>`}
              </CodeExample>
            </DocsCard>
          </div>

          <div className="domino-ui-docs-grid">
            <DocsCard title="Dimensioni e Layout">
              <p className="mt0 dark-gray">
                `width` e `height` accettano numeri o stringhe CSS. Se passi un
                numero, il componente usa pixel.
              </p>
              <div className="domino-ui-docs-example">
                <BlockSkeleton width={120} height={24} className="mb2" />
                <BlockSkeleton width="70%" height={18} className="mb2" />
                <BlockSkeleton width="100%" height={56} borderRadius={12} />
              </div>
              <CodeExample>
                {`<BlockSkeleton width={120} height={24} />
<BlockSkeleton width="70%" height={18} />
<BlockSkeleton width="100%" height={56} borderRadius={12} />`}
              </CodeExample>
            </DocsCard>

            <DocsCard title="LineChartSkeleton">
              <p className="mt0 dark-gray">
                Per trend e confronti temporali. Mantiene assi, griglia,
                legenda e altezza del chart reale.
              </p>
              <div className="domino-ui-docs-example">
                <LineChartSkeleton tone="soft" height={320} seriesCount={3} />
              </div>
              <CodeExample>
                {`<LineChartSkeleton
  tone="soft"
  height={320}
  seriesCount={3}
/>`}
              </CodeExample>
            </DocsCard>

            <DocsCard title="Tone Custom Locale">
              <p className="mt0 dark-gray">
                Quando i tone standard non bastano, aggiungi una classe custom
                che sovrascrive solo le due variabili colore.
              </p>
              <div className="domino-ui-docs-example">
                <BlockSkeleton
                  className="domino-ui-docs-skeleton-custom"
                  height={72}
                />
              </div>
              <CodeExample>
                {`.domino-ui-docs-skeleton-custom {
  --domino-skeleton-base-color: #dfe6ea;
  --domino-skeleton-highlight-color: #f8fafb;
}

<BlockSkeleton
  className="domino-ui-docs-skeleton-custom"
  height={72}
/>`}
              </CodeExample>
            </DocsCard>
          </div>

          <div className="domino-ui-docs-grid mt4">
            <DocsCard title="Sequenza Caricamento">
              <p className="mt0 dark-gray">
                Se una pagina deve mostrare prima un modulo principale e poi i
                pannelli dipendenti, usa uno stato di sblocco esplicito. Questo
                evita fetch parallele che rendono invisibili gli skeleton.
              </p>
              <CodeExample>
                {`const [headerLoaded, setHeaderLoaded] = useState(false);

useEffect(() => {
  if (!token || !headerLoaded) return;
  loadSecondaryModules();
}, [token, headerLoaded]);

<MainModule onLoad={() => setHeaderLoaded(true)} />`}
              </CodeExample>
            </DocsCard>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UISkeletonDocsPage;
